/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import dayjs, { Dayjs } from "dayjs"
import {
  Category,
  GroupedServiceHour,
  SellingTime,
  SellingTimeMetaData,
  SubDepartment,
} from "types/models/menu"
import { ResponseWithError, ServiceHours } from "types/api"
import { convertDateTZ } from "utils/date_formatter"

// order of days to display
export const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"]

export enum defaultTiming {
  START = "00:00",
  END = "23:59",
}

export enum openPeriodTypes {
  OPEN_ALL_DAY = "OpenAllDay",
  CLOSED_ALL_DAY = "ClosedAllDay",
  PERIOD = "Period",
}

export enum defaultPeriod {
  startTime = "1000-01-01T00:00:00Z",
  endTime = "9999-12-31T23:59:59Z",
}

export enum availabilityStatus {
  pending = "PENDING",
  active = "ACTIVE",
  deactivated = "DEACTIVATED",
  archived = "ARCHIVED",
  softdeleted = "SOFTDELETED",
}

export enum EventConflictStatus {
  noConflict = "NO_CONFLICT",
  needConfirmation = "NEED_CONFIRMATION",
  confirmed = "CONFIRMED",
  cancelled = "CANCELLED",
}

/*
create period map with days as value
eg.
{
  "10:00-18:00": ["sunday", "monday", "tuesday", "wednesday"],
  "11:00-18:00": ["thursday", "friday", "saturday"]
} 
*/
const mapPeriods = (serviceHours: ServiceHours) => {
  const periodsMap: {
    [key: string]: string[]
  } = {}

  Object.keys(serviceHours).forEach(day => {
    const { periods } = serviceHours[day]
    if (periods) {
      periods.forEach(period => {
        const { startTime, endTime } = period

        const key = `${startTime}-${endTime}`

        periodsMap[key] = [...(periodsMap[key] ?? []), day]
      })
    } else if (
      serviceHours[day].openPeriodType === openPeriodTypes.OPEN_ALL_DAY
    ) {
      const key = `${defaultTiming.START}-${defaultTiming.END}`

      periodsMap[key] = [...(periodsMap[key] ?? []), day]
    }
  })

  return periodsMap
}

const getDayRange = (startDay: string, endDay: string) => {
  if (startDay === endDay) {
    return startDay
  }
  return `${startDay}-${endDay}`
}

/*
create day ranges with available days as value
eg.
["mon-wed", "thu-sun"]
*/
const getDayRanges = (availableDays: string[]) => {
  if (availableDays.length === 1) {
    return [availableDays[0]]
  }
  const availableDaysMap = days.map(day =>
    availableDays.indexOf(day) > -1 ? day : "",
  )
  const dayRanges: string[] = []
  let startDay = ""
  let endDay = ""
  availableDaysMap.forEach((day, i) => {
    if (day) {
      if (!startDay) {
        startDay = day
      }
      if (i === availableDaysMap.length - 1) {
        endDay = day
        dayRanges.push(getDayRange(startDay, endDay))
      }
    } else {
      if (availableDaysMap[i - 1]) {
        endDay = availableDaysMap[i - 1]
        dayRanges.push(getDayRange(startDay, endDay))
      }
      startDay = ""
    }
  })
  return dayRanges
}

/*
create day grouped service hours with service hours from BE as value
eg.
{
  dayRange: "mon-wed";
  periods: ["09:00-11:00", "13:00-17:00"];
}[]
*/
export const getGroupedServiceHours = (serviceHours: ServiceHours) => {
  const periodsMap = mapPeriods(serviceHours)
  const mapDayRanges: {
    [key: string]: string[]
  } = {}

  Object.keys(periodsMap).forEach(period => {
    const daysAvailable = periodsMap[period]

    const dayRanges = getDayRanges(daysAvailable)

    dayRanges.forEach(dayRange => {
      mapDayRanges[dayRange] = [...(mapDayRanges[dayRange] ?? []), period]
    })
  })

  // change to array to sort
  const groupedServiceHoursArr = Object.entries(mapDayRanges)
  groupedServiceHoursArr.sort((a, b) => {
    const startDayA = a[0].split("-")[0]
    const startDayB = b[0].split("-")[0]
    if (startDayA !== startDayB) {
      return days.indexOf(startDayA) - days.indexOf(startDayB)
    }
    const endDayA = a[0].split("-")[1] || ""
    const endDayB = b[0].split("-")[1] || ""
    return days.indexOf(endDayA) - days.indexOf(endDayB)
  })
  const groupedServiceHours = groupedServiceHoursArr.map(
    groupedServiceHoursArrItem => ({
      dayRange: groupedServiceHoursArrItem[0],
      periods: groupedServiceHoursArrItem[1],
    }),
  )
  return groupedServiceHours
}

// generated translation keys and get value from phrase app map
export const useDaysText = (useDaysFullName?: boolean) => {
  const daysArray = days
  const translationKey = useDaysFullName ? "fulldaynames" : "common.days"

  const translationsMap = daysArray.reduce(
    (acc: { [key: string]: string }, day) => ({
      ...acc,
      [day]: `${translationKey}.${day}`,
    }),
    {},
  )

  return useTranslationsMapTyped(translationsMap)
}

/*
translate grouped service hours
eg.
{
  dayRange: "Mon - Wed",
  periods: ["Store Open Time - 11:00", "13:00 - Store Close Time"]
}
*/
export const useTranslatedGroupedServiceHours = (
  groupedServiceHours?: GroupedServiceHour[],
  useDaysFullName?: boolean,
): GroupedServiceHour[] => {
  const textMap = useTranslationsMapTyped({
    storeOpeningTime: `menu.sellingTime.label.storeOpeningTime`,
    storeClosingTime: `menu.sellingTime.label.storeClosingTime`,
  })

  const daysText = useDaysText(useDaysFullName)

  return (
    groupedServiceHours?.map(groupedServiceHour => {
      let translatedDayRange = ""

      const { dayRange, periods } = groupedServiceHour

      const startDay = dayRange.split("-")[0]
      const endDay = dayRange.split("-")[1]
      const translatedStartDay = daysText[startDay]
      if (endDay) {
        const translatedEndDay = daysText[endDay]
        translatedDayRange = `${translatedStartDay} - ${translatedEndDay}`
      } else {
        translatedDayRange = translatedStartDay
      }

      const translatedPeriods = periods.map(period => {
        let translatedStartTime = period.split("-")[0]
        let translatedEndTime = period.split("-")[1]
        if (translatedStartTime === defaultTiming.START) {
          translatedStartTime = textMap.storeOpeningTime
        }
        if (translatedEndTime === defaultTiming.END) {
          translatedEndTime = textMap.storeClosingTime
        }
        return `${translatedStartTime} - ${translatedEndTime}`
      })
      return {
        dayRange: translatedDayRange,
        periods: translatedPeriods,
      }
    }) ?? []
  )
}

export interface Timing {
  startTime: string
  endTime: string
}

export interface Availability {
  days: string[]
  timings: Timing[]
}

export interface SellingTimeFormValues {
  name: string
  availabilities: Availability[]
  period?: [Dayjs, Dayjs]
}

export const convertSellingTimeFormValuesToSellingTime = (
  formValues: SellingTimeFormValues,
  storeHourText: {
    startTime: string
    endTime: string
  },
  timezone: string,
) => {
  const { name, availabilities, period } = formValues

  const serviceHours: ServiceHours = {}

  // convert available days
  availabilities.forEach(availability => {
    availability.days.forEach(day => {
      const { timings } = availability
      const { startTime, endTime } = timings[0]

      if (
        (startTime === defaultTiming.START && endTime === defaultTiming.END) ||
        (startTime === storeHourText.startTime &&
          endTime === storeHourText.endTime)
      ) {
        serviceHours[day] = {
          openPeriodType: openPeriodTypes.OPEN_ALL_DAY,
        }
      } else {
        serviceHours[day] = {
          openPeriodType: openPeriodTypes.PERIOD,
          periods: timings
            .filter(
              // clear undefined times
              timing => timing.startTime && timing.endTime,
            ) // convert store time texts to timing
            .map(timing => ({
              startTime:
                timing.startTime === storeHourText.startTime
                  ? defaultTiming.START
                  : timing.startTime,
              endTime:
                timing.endTime === storeHourText.endTime
                  ? defaultTiming.END
                  : timing.endTime,
            })),
        }
      }
    })
  })

  // convert unavailable days
  days.forEach(day => {
    if (!serviceHours[day]) {
      serviceHours[day] = {
        openPeriodType: openPeriodTypes.CLOSED_ALL_DAY,
      }
    }
  })

  let startTime = ""
  let endTime = ""

  // convert period start time and end time
  if (period) {
    startTime = dayjs
      .utc(period[0])
      .tz(timezone)
      .set("hour", 0)
      .set("minute", 0)
      .set("second", 0)
      .format()
    endTime = dayjs
      .utc(period[1])
      .tz(timezone)
      .set("hour", 23)
      .set("minute", 59)
      .set("second", 59)
      .format()
  } else {
    startTime = defaultPeriod.startTime
    endTime = defaultPeriod.endTime
  }

  return {
    name,
    serviceHours,
    startTime,
    endTime,
  }
}

/*
create grouped period map with days as value
eg.
{
  "09:00-11:00,13:00-17:00": ["sunday", "monday", "tuesday", "wednesday"]
}
*/
const mapGroupedPeriods = (serviceHours: ServiceHours) => {
  const groupedPeriodsMap: {
    [key: string]: string[]
  } = {}

  Object.keys(serviceHours).forEach(day => {
    const { periods } = serviceHours[day]
    if (periods) {
      const keys: string[] = []
      periods.forEach(period => {
        const { startTime, endTime } = period
        keys.push(`${startTime}-${endTime}`)
      })
      const key = keys.join(",")
      groupedPeriodsMap[key] = [...(groupedPeriodsMap[key] ?? []), day]
    } else if (
      serviceHours[day].openPeriodType === openPeriodTypes.OPEN_ALL_DAY
    ) {
      const key = `${defaultTiming.START}-${defaultTiming.END}`

      groupedPeriodsMap[key] = [...(groupedPeriodsMap[key] ?? []), day]
    }
  })

  return groupedPeriodsMap
}

export const getHasPeriod = (sellingTime?: SellingTime) =>
  !(
    sellingTime &&
    sellingTime.startTime === defaultPeriod.startTime &&
    sellingTime.endTime === defaultPeriod.endTime
  )

export const convertSellingTimeToSellingTimeFormValues = (
  sellingTime: SellingTime,
  timezone: string,
): SellingTimeFormValues => {
  const { name, startTime, endTime, serviceHours } = sellingTime
  const groupedPeriodsMap = mapGroupedPeriods(serviceHours)

  const availabilities: Availability[] = Object.keys(groupedPeriodsMap).map(
    groupedPeriod => ({
      days: groupedPeriodsMap[groupedPeriod],
      timings: groupedPeriod.split(",").map(period => ({
        startTime: period.split("-")[0],
        endTime: period.split("-")[1],
      })),
    }),
  )

  const result: SellingTimeFormValues = {
    name,
    availabilities,
  }
  if (getHasPeriod(sellingTime)) {
    result.period = [
      convertDateTZ(startTime, timezone),
      convertDateTZ(endTime, timezone),
    ]
  }
  return result
}

export const createErrorCallback = (response: ResponseWithError) => {
  const { message = "" } = response ?? {}

  return message
}

export const updateErrorCallback = (response: ResponseWithError) => {
  const { message = "" } = response ?? {}

  return message
}

export const deleteErrorCallback = (response: ResponseWithError) => {
  const { message = "" } = response ?? {}

  return message
}

export const alwaysAvailableID = "AlwaysAvailable"
export const alwaysAvailableServiceHours = {
  mon: {
    openPeriodType: openPeriodTypes.OPEN_ALL_DAY,
  },
  tue: {
    openPeriodType: openPeriodTypes.OPEN_ALL_DAY,
  },
  wed: {
    openPeriodType: openPeriodTypes.OPEN_ALL_DAY,
  },
  thu: {
    openPeriodType: openPeriodTypes.OPEN_ALL_DAY,
  },
  fri: {
    openPeriodType: openPeriodTypes.OPEN_ALL_DAY,
  },
  sat: {
    openPeriodType: openPeriodTypes.OPEN_ALL_DAY,
  },
  sun: {
    openPeriodType: openPeriodTypes.OPEN_ALL_DAY,
  },
}
export const dummyAlwaysAvailableSellingTime: SellingTime = {
  sellingTimeID: alwaysAvailableID,
  name: "Always Available",
  serviceHours: alwaysAvailableServiceHours,
  startTime: defaultPeriod.startTime,
  endTime: defaultPeriod.endTime,
  status: availabilityStatus.active,
  updatedAt: "",
  createdAt: "",
  groupedServiceHours: getGroupedServiceHours(alwaysAvailableServiceHours),
}
export const getIsAlwaysAvailable = (sellingTime?: SellingTime) => {
  if (!sellingTime) return false
  const is247 = Object.values(sellingTime.serviceHours).every(
    serviceHour =>
      serviceHour.openPeriodType === openPeriodTypes.OPEN_ALL_DAY ||
      (serviceHour.periods?.[0] &&
        serviceHour.periods[0].startTime === defaultTiming.START &&
        serviceHour.periods[0].endTime === defaultTiming.END),
  )
  const hasPeriod = getHasPeriod(sellingTime)
  return is247 && !hasPeriod
}

export const allSellingTimeID = "all"
export const allSellingTime: SellingTime = {
  // dummy selling time with "all" ID if dropdown used for filter
  ...dummyAlwaysAvailableSellingTime,
  sellingTimeID: allSellingTimeID,
}
export const getIsAllSellingTime = (sellingTime?: SellingTime) =>
  sellingTime?.sellingTimeID === allSellingTimeID

export const getNormalizedSellingTimes = (sellingTimes?: SellingTime[]) => {
  if (!sellingTimes) {
    return []
  }
  let result: SellingTime[] = sellingTimes.map(sellingTime => ({
    ...sellingTime,
    groupedServiceHours: getGroupedServiceHours(sellingTime.serviceHours),
  }))
  const hasAlwaysAvailable = result.some(sellingTime =>
    getIsAlwaysAvailable(sellingTime),
  )
  // if selling times don't have "always available", add "always available dummy"
  if (!hasAlwaysAvailable) {
    result.push(dummyAlwaysAvailableSellingTime)
  }
  result = result.sort((a, b) => {
    if (getIsAlwaysAvailable(a)) return -1
    if (getIsAlwaysAvailable(b)) return 1
    return dayjs.utc(a.createdAt).diff(dayjs.utc(b.createdAt))
  })
  return result
}

export const getSellingTimeByID = (
  sellingTimeID?: string,
  sellingTimes?: SellingTime[],
) => {
  return sellingTimes?.find(
    sellingTime => sellingTime.sellingTimeID === sellingTimeID,
  )
}

export const getTranslatedPeriodTimes = (startTime: Dayjs, endTime: Dayjs) => ({
  startTime: startTime.format(
    startTime.year() === endTime.year() ? "DD MMM" : "DD MMM YYYY",
  ),
  endTime: endTime.format("DD MMM YYYY"),
})

const generateTimingOptions = () => {
  const result: {
    value: string
  }[] = []

  for (let i = 0; i < 24; i += 1) {
    const hour = `${i}`.padStart(2, "0")
    const minutes = ["00", "15", "30"]
    const hourMinuteOptions = minutes.map(minute => ({
      value: `${hour}:${minute}`,
    }))
    result.push(...hourMinuteOptions.filter(option => option.value !== "00:00"))
  }
  return result
}

const timingOptions = generateTimingOptions()

export const getTimingOptions = (
  storeHourOption: {
    value: string
  },
  value?: string,
) => {
  const result = [storeHourOption, ...timingOptions]
  if (!value) {
    return result
  }
  return result.filter(option =>
    option.value.toLocaleLowerCase().includes(value.toLocaleLowerCase()),
  )
}

export const useConflictConfirmPayload = (
  itemParent?: Category | SubDepartment | null,
) => {
  const textMap = useTranslationsMapTyped({
    updateCategoryConflictTitle:
      "menu.sellingTime.modal.updateCategoryConflict.title",
    updateCategoryConflictDescription:
      "menu.sellingTime.modal.updateCategoryConflict.description",
    updateCategoryConflictOk:
      "menu.sellingTime.modal.updateCategoryConflict.button.ok",
    cancelButton: "menu.buttons.cancel",
  })
  const isConflict =
    itemParent?.sellingTimeID &&
    itemParent.items?.some(
      item => item.sellingTimeID !== itemParent.sellingTimeID,
    )
  // if some items have different selling time than this category, show confirm modal first
  if (isConflict) {
    return {
      showConfirm: true,
      confirmOptions: {
        title: textMap.updateCategoryConflictTitle,
        content: textMap.updateCategoryConflictDescription,
        okText: textMap.updateCategoryConflictOk,
        cancelText: textMap.cancelButton,
      },
    }
  }
  return {}
}

export const getSellingTimeMetadata = (
  sellingTime: SellingTime,
): SellingTimeMetaData => {
  const { groupedServiceHours, ...sellingTimeMetaData } = sellingTime
  return sellingTimeMetaData
}

export const getSellingTimeEventInfo = (sellingTime: SellingTime) => {
  const { sellingTimeID, name, serviceHours } = sellingTime
  return {
    SELLING_TIME_ID: sellingTimeID,
    SELLING_TIME_NAME: name,
    SELLING_TIME_INFO: getSellingTimeMetadata(sellingTime),
    SERVICE_HOURS: serviceHours,
  }
}
